import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  InputAdornment,
  Badge,
  useTheme
} from '@mui/material';
import { useAuth } from '../AuthContext';
import axios from '../../config/axiosConfig';

// Icons
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TelegramIcon from '@mui/icons-material/Telegram';
import ChatIcon from '@mui/icons-material/Chat';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyIcon from '@mui/icons-material/Key';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const ChannelSettings = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [dialogMode, setDialogMode] = useState('setup'); // 'setup' or 'edit'
  const [channelConfig, setChannelConfig] = useState({});
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [, setVerificationCode] = useState('');
  const [showWebhook, setShowWebhook] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);

  // Plan information
  const userPlan = user?.plan?.name?.toLowerCase() || 'free';
  const isPremium = userPlan === 'premium';
  const isBasic = userPlan === 'basic';
  const isFree = userPlan === 'free';

  // Channel definitions with plan requirements
  const channelDefinitions = {
    site: {
      name: 'Site Notifications',
      icon: <NotificationsIcon />,
      color: '#2196F3',
      description: 'Receive notifications in your dashboard',
      planRequired: 'free',
      setup: false,
      fields: []
    },
    email: {
      name: 'Email',
      icon: <EmailIcon />,
      color: '#4CAF50',
      description: 'Get alerts delivered to your email inbox',
      planRequired: 'basic',
      setup: true,
      fields: [] // Changed from original
    },
    telegram: {
      name: 'Telegram',
      icon: <TelegramIcon />,
      color: '#2CA5E0',
      description: 'Receive instant notifications on Telegram',
      planRequired: 'basic',
      setup: true,
      setupInstructions: true,
      fields: []
    },
    discord: {
      name: 'Discord',
      icon: <ChatIcon />,
      color: '#7289DA',
      description: 'Send alerts to your Discord server',
      planRequired: 'premium',
      setup: true,
      fields: [
        {
          name: 'webhookUrl',
          label: 'Webhook URL',
          type: 'text',
          required: true,
          placeholder: 'https://discord.com/api/webhooks/...',
          validation: (value) => {
            if (!value.startsWith('https://discord.com/api/webhooks/')) {
              return 'Invalid Discord webhook URL';
            }
            return null;
          }
        }
      ]
    },
    whatsapp: {
      name: 'WhatsApp',
      icon: <WhatsAppIcon />,
      color: '#25D366',
      description: 'Get notifications on WhatsApp (Coming Soon)',
      planRequired: 'premium',
      comingSoon: true,
      setup: false,
      fields: []
    }
  };

  // Check if user has access to a channel
  const hasAccess = (channel) => {
    const requiredPlan = channelDefinitions[channel]?.planRequired;

    if (requiredPlan === 'free') return true;
    if (requiredPlan === 'basic' && (isBasic || isPremium)) return true;
    if (requiredPlan === 'premium' && isPremium) return true;

    return false;
  };

  // Get plan display name
  const getPlanDisplayName = (plan) => {
    const planName = plan.charAt(0).toUpperCase() + plan.slice(1);
    return planName;
  };

  // Load user channels
  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/user/channels');
      setChannels(response.data);
    } catch (error) {
      console.error('Error fetching channels:', error);
      setErrorMessage('Failed to load channel settings');
    } finally {
      setLoading(false);
    }
  };

  // Handle channel setup/edit dialog
  const handleOpenDialog = (channelType, mode = 'setup') => {
    const existingChannel = channels.find(ch => ch.type === channelType);

    setSelectedChannel(channelType);
    setDialogMode(mode);
    setChannelConfig(existingChannel?.config || {});
    setErrors({});

    // For Telegram, generate a new verification code if needed
    if (channelType === 'telegram' && mode === 'setup' && !existingChannel) {
      generateVerificationCode();
    }

    setOpenDialog(true);
  };

  // Generate verification code for Telegram
  const generateVerificationCode = () => {
    // Generate a random 6-digit code
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
  };

  // Handle closing dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedChannel(null);
    setChannelConfig({});
    setErrors({});
    setVerificationCode('');
  };

  // Handle channel configuration save
  const handleSaveChannel = async () => {
    if (!selectedChannel) return;

    // Validate fields
    const definition = channelDefinitions[selectedChannel];
    const validationErrors = {};

    definition.fields?.forEach(field => {
      const value = channelConfig[field.name];

      if (field.required && !value) {
        validationErrors[field.name] = `${field.label} is required`;
      } else if (field.validation && value) {
        const error = field.validation(value);
        if (error) {
          validationErrors[field.name] = error;
        }
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Telegram validation
    if (selectedChannel === 'telegram' && !isPremium && channelConfig.chatId?.startsWith('-')) {
      setErrorMessage('Group/channel notifications require Premium plan. Basic plan only supports personal chats.');
      return;
    }

    setSaving(true);
    try {
      let configToSave = { ...channelConfig };

      // Special handling for Email
      if (selectedChannel === 'email') {
        configToSave = {
          address: user?.email
        };
        // Mark as verified when user confirms
        if (dialogMode === 'setup') {
          configToSave.verifiedAt = new Date();
        }
      }

      // Special handling for Telegram
      if (selectedChannel === 'telegram' && dialogMode === 'setup') {
        // Auto-verify when setting up Telegram
        configToSave.verifiedAt = new Date();
      }

      await axios.post('/api/user/channels', {
        type: selectedChannel,
        config: configToSave,
        isActive: true,
        isVerified: (selectedChannel === 'email' || selectedChannel === 'telegram') && dialogMode === 'setup'
      });

      // Refresh channels
      await fetchChannels();

      setSuccessMessage(`${definition.name} channel ${dialogMode === 'setup' ? 'configured' : 'updated'} successfully`);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving channel:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to save channel configuration');
    } finally {
      setSaving(false);
    }
  };

  // Handle channel toggle
  const handleToggleChannel = async (channelId, currentStatus) => {
    try {
      await axios.put(`/api/user/channels/${channelId}`, {
        isActive: !currentStatus
      });

      // Refresh channels
      await fetchChannels();

      setSuccessMessage(`Channel ${currentStatus ? 'disabled' : 'enabled'} successfully`);
    } catch (error) {
      console.error('Error toggling channel:', error);
      setErrorMessage('Failed to update channel status');
    }
  };

  // Handle channel deletion
  const handleDeleteChannel = (channelId) => {
    setChannelToDelete(channelId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!channelToDelete) return;

    try {
      await axios.delete(`/api/user/channels/${channelToDelete}`);

      // Refresh channels
      await fetchChannels();

      setSuccessMessage('Channel configuration deleted successfully');
    } catch (error) {
      console.error('Error deleting channel:', error);
      setErrorMessage('Failed to delete channel configuration');
    } finally {
      setDeleteDialogOpen(false);
      setChannelToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setChannelToDelete(null);
  };

  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copied to clipboard');
  };

  // Render channel card
  const renderChannelCard = (channelType) => {
    const definition = channelDefinitions[channelType];
    const existingChannel = channels.find(ch => ch.type === channelType);
    const hasAccessToChannel = hasAccess(channelType);
    const isConfigured = !!existingChannel;
    const isActive = existingChannel?.isActive;
    const isVerified = existingChannel?.isVerified;

    return (
      <Grid item xs={12} md={6} key={channelType}>
        <Card
          sx={{
            height: '100%',
            opacity: hasAccessToChannel ? 1 : 0.7,
            position: 'relative',
            border: '1px solid',
            borderColor: isActive && isVerified ? definition.color : 'divider',
            transition: 'all 0.2s'
          }}
        >
          {!hasAccessToChannel && !definition.comingSoon && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                borderRadius: 1
              }}
            >
              <LockIcon sx={{ fontSize: 60, opacity: 0.3 }} />
            </Box>
          )}

          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ color: definition.color, mr: 2 }}>
                  {definition.icon}
                </Box>
                <Box>
                  <Typography variant="h6">
                    {definition.name}
                  </Typography>
                  {!hasAccessToChannel && (
                    <Chip
                      size="small"
                      icon={<LockIcon />}
                      label={`Requires ${getPlanDisplayName(definition.planRequired)} Plan`}
                      color="default"
                    />
                  )}
                </Box>
              </Box>

              {hasAccessToChannel && isConfigured && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={() => handleToggleChannel(existingChannel._id, isActive)}
                      disabled={!isVerified || channelType === 'site'}
                    />
                  }
                  label=""
                />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph>
              {definition.description}
            </Typography>

            {hasAccessToChannel && (
              <>
                {isConfigured ? (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {isVerified ? (
                        <Chip
                          size="small"
                          icon={<CheckCircleIcon />}
                          label="Verified"
                          color="success"
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<WarningIcon />}
                          label="Unverified"
                          color="warning"
                        />
                      )}
                      {isActive ? (
                        <Chip
                          size="small"
                          icon={<CheckCircleIcon />}
                          label="Active"
                          color="primary"
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<ErrorIcon />}
                          label="Inactive"
                          color="default"
                        />
                      )}
                    </Box>

                    {channelType === 'email' && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Email:</strong> {user?.email || 'Not available'}
                        </Typography>
                        {!isVerified && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              Please confirm your email to start receiving notifications
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenDialog(channelType, 'setup')}
                              sx={{ mt: 1 }}
                            >
                              Confirm Email
                            </Button>
                          </Alert>
                        )}
                      </Box>
                    )}

                    {channelType !== 'site' && channelType !== 'email' && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        {definition.setup && (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenDialog(channelType, 'edit')}
                          >
                            Configure
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteChannel(existingChannel._id)}
                        >
                          Remove
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    {channelType === 'site' ? (
                      <Alert severity="success">
                        Automatically configured for all users
                      </Alert>
                    ) : channelType === 'email' && isConfigured && isVerified ? (
                      <Alert severity="success">
                        Email notifications enabled for {user?.email}
                      </Alert>
                    ) : definition.comingSoon ? (
                      <Alert severity="info">
                        Coming soon! This channel is not available yet.
                      </Alert>
                    ) : (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={channelType === 'email' ? <EmailIcon /> : <SettingsIcon />}
                        onClick={() => handleOpenDialog(channelType, 'setup')}
                        disabled={!definition.setup}
                      >
                        {channelType === 'email' ? 'Enable Email Notifications' : 'Set Up Channel'}
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}

            {!hasAccessToChannel && !definition.comingSoon && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => window.location.href = '/pricing'}
                >
                  Upgrade to {getPlanDisplayName(definition.planRequired)}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Render setup dialog content
  const renderDialogContent = () => {
    if (!selectedChannel) return null;

    const definition = channelDefinitions[selectedChannel];

    if (selectedChannel === 'email') {
      return (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please confirm that you want to enable email notifications for the following address:
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
              border: `1px solid ${theme.palette.divider}`,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              }}
            >
              {user?.email || 'No email address found in your profile.'}
            </Typography>
          </Paper>
          {!user?.email && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your email address is not available in your profile.
              Please update your profile information to enable email notifications.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Click "{dialogMode === 'setup' ? 'Confirm & Enable' : 'Save Configuration'}" to proceed.
          </Typography>
        </Box>
      );
    }

    if (selectedChannel === 'telegram') {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Follow these steps to set up Telegram notifications:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <Badge badgeContent="1" color="primary">
                  <TelegramIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary="Start a chat with our bot"
                secondary="@MarketRodeoBot"
              />
              <IconButton
                size="small"
                onClick={() => copyToClipboard('@MarketRodeoBot')}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Badge badgeContent="2" color="primary">
                  <ChatIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary="Send the /start command"
                secondary="The bot will reply with your Chat ID"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Badge badgeContent="3" color="primary">
                  <KeyIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary="Enter your Chat ID below"
                secondary="Copy the Chat ID from the bot's response"
              />
            </ListItem>
          </List>

          <TextField
            fullWidth
            label="Telegram Chat ID"
            variant="outlined"
            margin="normal"
            value={channelConfig.chatId || ''}
            onChange={(e) => setChannelConfig({ ...channelConfig, chatId: e.target.value })}
            placeholder="123456789"
            helperText="Enter the Chat ID you received from the bot"
          />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              href="https://t.me/MarketRodeoBot"
              target="_blank"
              startIcon={<TelegramIcon />}
            >
              Open Telegram Bot
            </Button>
          </Box>

          {isBasic && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note for Basic Plan users:</strong> This bot is for personal notifications only.
                Bot access is restricted to your account.
              </Typography>
            </Alert>
          )}
        </Box>
      );
    }

    if (selectedChannel === 'discord') {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Discord Webhook Setup
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <Badge badgeContent="1" color="primary">
                  <SettingsIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary="Open Server Settings"
                secondary="Go to your Discord server and click on Server Settings"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Badge badgeContent="2" color="primary">
                  <ChatIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary="Navigate to Integrations"
                secondary="Find the Integrations section in the settings menu"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Badge badgeContent="3" color="primary">
                  <KeyIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary="Create Webhook"
                secondary="Create a new webhook and copy the URL"
              />
            </ListItem>
          </List>

          <TextField
            fullWidth
            label="Discord Webhook URL"
            variant="outlined"
            margin="normal"
            value={channelConfig.webhookUrl || ''}
            onChange={(e) => setChannelConfig({ ...channelConfig, webhookUrl: e.target.value })}
            error={!!errors.webhookUrl}
            helperText={errors.webhookUrl}
            placeholder="https://discord.com/api/webhooks/..."
            InputProps={{
              endAdornment: showWebhook ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowWebhook(false)}
                    edge="end"
                  >
                    <VisibilityOffIcon />
                  </IconButton>
                </InputAdornment>
              ) : (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowWebhook(true)}
                    edge="end"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            type={showWebhook ? 'text' : 'password'}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Make sure the webhook has permission to send messages in the selected channel.
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (definition.fields && definition.fields.length > 0) {
      return (
        <Box>
          {definition.fields?.map((field) => (
            <TextField
              key={field.name}
              fullWidth
              label={field.label}
              variant="outlined"
              margin="normal"
              type={field.type}
              value={channelConfig[field.name] || ''}
              onChange={(e) => setChannelConfig({
                ...channelConfig,
                [field.name]: e.target.value
              })}
              error={!!errors[field.name]}
              helperText={errors[field.name]}
              placeholder={field.placeholder}
              required={field.required}
            />
          ))}
        </Box>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        {definition.description || `Configure ${definition.name}.`}
        {(!definition.fields || definition.fields.length === 0) &&
          !definition.setupInstructions &&
          selectedChannel !== 'email' &&
          (
            <Typography sx={{ mt: 2 }}>
              No specific configuration is needed for this channel. Click "{dialogMode === 'setup' ? 'Enable' : 'Save'}" to activate/update.
            </Typography>
          )}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Notification Channel Settings
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Configure how you receive notifications for your alerts. Different channels are available based on your subscription plan.
      </Typography>

      <Alert
        severity="info"
        sx={{ mb: 4 }}
        icon={<InfoIcon />}
      >
        <Typography variant="body2">
          <strong>Your Plan:</strong> {getPlanDisplayName(userPlan)} •{' '}
          {isFree && 'Site notifications only'}
          {isBasic && 'Site, Email, and Telegram notifications'}
          {isPremium && 'All notification channels available'}
          {!isPremium && (
            <>
              {' • '}
              <a href="/pricing" style={{ color: theme.palette.primary.main }}>
                View upgrade options
              </a>
            </>
          )}
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {Object.keys(channelDefinitions).map(channelType =>
          renderChannelCard(channelType)
        )}
      </Grid>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(19, 47, 76, 0.95)'
              : 'rgba(255, 255, 255, 0.98)',
            backgroundImage: 'none',
            backdropFilter: 'blur(10px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>
          {selectedChannel && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ color: channelDefinitions[selectedChannel].color, mr: 1 }}>
                {channelDefinitions[selectedChannel].icon}
              </Box>
              {dialogMode === 'setup' ? 'Set Up' : 'Configure'} {channelDefinitions[selectedChannel].name}
            </Box>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {renderDialogContent()}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveChannel}
            variant="contained"
            disabled={
              saving ||
              (selectedChannel === 'email' && !user?.email)
            }
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' :
              selectedChannel === 'email' && dialogMode === 'setup' ? 'Confirm & Enable' :
                'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(19, 47, 76, 0.95)'
              : 'rgba(255, 255, 255, 0.98)',
            backgroundImage: 'none',
            backdropFilter: 'blur(10px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            maxWidth: 400,
            mx: 'auto'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />
            Delete Channel Configuration
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this channel configuration?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChannelSettings;